// React Router 7 route tree (ADR-014 §2). The SAME EmployeeListContent
// renders under the SINGLE AppShell in BOTH scopes — /people (tenant-scope)
// and /me/directory (self-scope). The shell is never forked and carries no
// role parameter (SEM-5131: the hat is gone — one menu, groups filtered by
// real authority inside LeftRail; the self/tenant split lives in the ROUTES,
// which the server scopes per caller).
//
// /callback stays OUTSIDE RequireAuth (it completes the OIDC redirect, so
// guarding it would loop). Minimal public certificate verification is also
// unguarded; all customer application surfaces require authentication.
//
// CODE-SPLITTING (perf sweep): the first-paint path — auth guard, AppShell,
// dashboard, and the /me basics (directory/leave/attendance/org/payslips/
// tasks) — stays eagerly imported. Heavy or infrequently-visited surfaces
// (payroll wizard, surveys, settings/roles, support, onboarding, benefits,
// assets, calendar, kudos, notification prefs) defer to their own chunks via
// lazyRoute below. Components keep their normal named exports in the feature
// files — ONLY the router import is dynamic, so tests/imports elsewhere are
// unaffected.

import type { ComponentType } from "react";

import { createBrowserRouter, Navigate } from "react-router";

import { AppShell } from "@/components/AppShell";
import { RequireAuth } from "@/auth/RequireAuth";
import { RequireMenuEntry } from "@/auth/RequireAuthority";
import {
  RouteErrorBoundary,
  RouteHydrateFallback,
} from "@/components/route-boundaries";
import { EmployeeDetailContent } from "@/content/employee-detail-content";
import { EmployeeListContent } from "@/content/employee-list-content";
import { LeaveListContent } from "@/content/leave-list-content";
import { LeaveBalanceContent } from "@/content/leave-balance-content";
import { LeaveCalendarContent } from "@/content/leave-calendar-content";
import { AttendanceContent } from "@/content/attendance-content";
import { AdminPdksContent } from "@/content/admin-pdks-content";
import { LeaveFilePage } from "@/features/leave/leave-file-page";
import { EmployeeFormPage } from "@/features/employees/employee-form-page";
import { PayslipContent } from "@/content/payslip-content";
import { OrgContent } from "@/features/org";
import { ORG_SCENARIOS_PATH } from "@/features/org-scenarios/paths";
// By direct path, not the `@/features/org-scenarios` barrel: that barrel is the
// lazy chunk this route defers to, and importing it here would pull the whole
// scenario surface back onto the eager first-paint graph.
import { RequireScenarioAccess } from "@/features/org-scenarios/require-scenario-access";
import { TasksContent, MyTasksContent } from "@/features/tasks";
import { DashboardPage } from "@/routes/dashboard";
import { CallbackPage } from "@/routes/callback";
import { CERTIFICATE_VERIFY_PATH } from "@/features/learning/certificate-paths";

/**
 * Defers a route Component to its own chunk (Vite splits on the dynamic
 * import). Every lazy route gets the shared skeleton fallback for the
 * initial-load case and a bounded retryable error boundary INSIDE the shell
 * (chunk-fetch failure or render crash never blanks the page — LeftRail/
 * TopBar stay interactive).
 */
function lazyRoute<M>(load: () => Promise<M>, pick: (mod: M) => ComponentType) {
  return {
    lazy: async () => ({ Component: pick(await load()) }),
    HydrateFallback: RouteHydrateFallback,
    ErrorBoundary: RouteErrorBoundary,
  };
}

export const router = createBrowserRouter([
  // OIDC callback — unguarded so the redirect can complete.
  { path: "/callback", Component: CallbackPage },
  { path: "/preboarding", ...lazyRoute(() => import("@/features/onboarding/preboarding-portal"), (m) => m.PreboardingPortalContent) },
  { path: CERTIFICATE_VERIFY_PATH, ...lazyRoute(() => import("@/features/learning/certificate-public"), (m) => m.PublicCertificatePage) },

  // Root — dashboard + the universal self-service surface.
  {
    path: "/",
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      // Faz 0.3 golden sample — novem dashboard (NovemHR-ekranlar port)
      // replaces the Phase-0 healthz landing page.
      { index: true, Component: DashboardPage },
      { path: "search", ...lazyRoute(() => import("@/features/global-search/search-content"), (m) => m.GlobalSearchPage) },
      { path: "experience", ...lazyRoute(() => import("@/features/experience/experience-page"), (m) => m.ExperiencePage) },
      { path: "me/feedback", ...lazyRoute(() => import("@/features/employee-feedback/pages"), (m) => m.MyFeedbackPage) },
      { path: "me/feedback/new", ...lazyRoute(() => import("@/features/employee-feedback/pages"), (m) => m.NewFeedbackPage) },
      { path: "me/feedback/:caseId", ...lazyRoute(() => import("@/features/employee-feedback/pages"), (m) => m.MyFeedbackCasePage) },
      { path: "employee-feedback", ...lazyRoute(() => import("@/features/employee-feedback/pages"), (m) => m.FeedbackInboxPage) },
      { path: "employee-feedback/:caseId", ...lazyRoute(() => import("@/features/employee-feedback/pages"), (m) => m.FeedbackCasePage) },
      { path: "employee-feedback/routing", ...lazyRoute(() => import("@/features/employee-feedback/pages"), (m) => m.FeedbackRoutingPage) },
      { path: "employee-feedback/categories", ...lazyRoute(() => import("@/features/employee-feedback/pages"), (m) => m.FeedbackCategoriesPage) },
      { path: "employee-feedback/identities", ...lazyRoute(() => import("@/features/employee-feedback/pages"), (m) => m.FeedbackIdentityPage) },
      { path: "employee-feedback/report", ...lazyRoute(() => import("@/features/employee-feedback/pages"), (m) => m.FeedbackReportPage) },
      { path: "me/staff-requisitions", ...lazyRoute(() => import("@/features/staff-requisitions/pages"), (m) => m.MyStaffRequisitionsPage) },
      { path: "me/staff-requisitions/new", ...lazyRoute(() => import("@/features/staff-requisitions/pages"), (m) => m.NewStaffRequisitionPage) },
      { path: "me/staff-requisitions/:id", ...lazyRoute(() => import("@/features/staff-requisitions/pages"), (m) => m.MyStaffRequisitionPage) },
      { path: "me/staff-requisitions/:id/edit", ...lazyRoute(() => import("@/features/staff-requisitions/pages"), (m) => m.EditStaffRequisitionPage) },
      { path: "staff-requisitions", ...lazyRoute(() => import("@/features/staff-requisitions/pages"), (m) => m.StaffRequisitionsManagePage) },
      { path: "staff-requisitions/:id", ...lazyRoute(() => import("@/features/staff-requisitions/pages"), (m) => m.StaffRequisitionManagePage) },
      { path: "me/reviews", ...lazyRoute(() => import("@/features/reviews/list"), (m) => m.ReviewListPage) },
      { path: "me/reviews/new", ...lazyRoute(() => import("@/features/reviews/create"), (m) => m.ReviewCreatePage) },
      { path: "me/reviews/templates/new", ...lazyRoute(() => import("@/features/reviews/template"), (m) => m.ReviewTemplatePage) },
      { path: "me/reviews/:id", ...lazyRoute(() => import("@/features/reviews/detail"), (m) => m.ReviewDetailPage) },
      { path: "me/review-responses/:id", ...lazyRoute(() => import("@/features/reviews/response"), (m) => m.ReviewResponsePage) },
      { path: "me/one-on-ones", ...lazyRoute(() => import("@/features/one-on-ones/list-content"), (m) => m.OneOnOneListContent) },
      { path: "me/one-on-ones/:id", ...lazyRoute(() => import("@/features/one-on-ones/route-children"), (m) => m.OneOnOneDetailRoute) },
      // Shell/Content worked example (employee role).
      { path: "me/directory", Component: EmployeeListContent },
      { path: "me/team", ...lazyRoute(() => import("@/features/employees/my-team-page"), (m) => m.MyTeamPage) },
      { path: "me/documents", ...lazyRoute(() => import("@/features/documents/my-documents-page"), (m) => m.MyDocumentsPage) },
      // P5/B4 dual-role (ADR-014 §2 no-copy): me-profile mounts the SAME
      // EmployeeListContent module as the admin /people twin, and that module
      // dispatches on the path to its OWN-PROFILE surface (MyProfileView) — the
      // drawn emp-profile.jsx screen, NOT the directory list. One module, one
      // shell, route-resolved surface; no forked profile screen. The server
      // scopes the row to the caller (employee_self); a self WRITE is denied
      // server-side (dual-role.ts self-exclusion).
      { path: "me/profile", Component: EmployeeListContent },
      // P5/B4 dual-role (ADR-014 §2 no-copy): me-requests renders the SAME
      // ApprovalListContent as the admin /requests twin, narrowed server-side
      // with `mine=true` so it is a self-history view even for a manager and
      // spans every approval-backed kind, not only leave.
      // SEM-6355: the request center's "Yeni talep" opens the type chooser
      // (emp-requests.jsx §3b), not a single type's form.
      {
        path: "me/requests",
        ...lazyRoute(() => import("@/features/approvals"), (m) => m.MyRequestsContent),
      },
      {
        path: "me/requests/new",
        ...lazyRoute(() => import("@/features/requests"), (m) => m.NewRequestContent),
      },
      // PHASE_1 / B0 leave reference slice — self context (`me-leave`). The
      // SAME LeaveListContent renders here and under /leave (admin twin); the
      // self surface sends the explicit server-side `mine=true` narrow
      // (ADR-014 §2 no-copy proof).
      { path: "me/leave", Component: LeaveListContent },
      // PHASE_1 / B1 leave self surfaces — file a request + own balances.
      { path: "me/leave/file", Component: LeaveFilePage },
      { path: "me/leave/balance", Component: LeaveBalanceContent },
      // Faz 8.6 / A3 — the monthly calendar view over the SAME leave-request
      // window (emp-leave-calendar.jsx port; ADR-014 §2 twin of /leave/calendar).
      { path: "me/leave/calendar", Component: LeaveCalendarContent },
      // PHASE_1 / B2 time/attendance — self clock-in/out + own entries.
      { path: "me/attendance", Component: AttendanceContent },
      // PHASE_1 / B3 org — read-only employee org chart (server scopes;
      // mutations denied). SAME OrgContent as the admin /org twin.
      { path: "me/org", Component: OrgContent },
      // PHASE_1 / B4 onboarding — the new hire's own onboarding journey.
      {
        path: "me/onboarding",
        ...lazyRoute(() => import("@/features/onboarding"), (m) => m.OnboardingContent),
      },
      { path: "me/offboarding", ...lazyRoute(() => import("@/features/offboarding"), (m) => m.EmployeeOffboardingContent) },
      { path: "me/offboarding/instances/:instanceId", ...lazyRoute(() => import("@/features/offboarding"), (m) => m.OffboardingInstanceContent) },
      { path: "me/offboarding/tasks/:taskId", ...lazyRoute(() => import("@/features/offboarding"), (m) => m.OffboardingTaskContent) },
      { path: "me/offboarding/interviews/:interviewId", ...lazyRoute(() => import("@/features/offboarding"), (m) => m.OffboardingInterviewContent) },
      // PHASE_3 / B1 assets — the employee's OWN assignments (Zimmetlerim,
      // employee_self; server-pinned). The in-screen sign affordance lives here.
      {
        path: "me/assets",
        ...lazyRoute(() => import("@/features/assets"), (m) => m.MyAssetsContent),
      },
      // PHASE_3 / B2 benefits — the employee's OWN benefits (Yan Haklarım,
      // me-benefits, employee_self; server-pinned): the published-plan catalog
      // (enroll/waive), my enrollments, and report-a-QLE. SAME building blocks
      // as the admin /benefits twin, role-swapped (ADR-014).
      {
        path: "me/benefits",
        ...lazyRoute(() => import("@/features/benefits"), (m) => m.MyBenefitsContent),
      },
      // PHASE_4 / B1 tasks — the employee's OWN task inbox (Görevlerim,
      // me-tasks, employee_self; server-pinned to own rows). Per-task
      // complete/snooze/dismiss + the deep-link "go to source" live here. SAME
      // building blocks as the admin /tasks twin, role-swapped (ADR-014).
      { path: "me/tasks", Component: MyTasksContent },
      { path: "me/tasks/:taskId", Component: MyTasksContent },
      // PHASE_4 / B7 calendar — the employee's OWN calendar (Takvimim,
      // my-calendar; the list sends `mine=true`, so the server returns events
      // the caller organizes OR is invited to, at every tier). Create-event + reschedule/cancel own events + .ics export live
      // here; reschedule/cancel stay ORGANIZER-only server-side (a leaked path
      // 403s). The free/busy finder + scheduling links live under /me/scheduling.
      {
        path: "me/calendar",
        ...lazyRoute(() => import("@/features/calendar"), (m) => m.MyCalendarContent),
      },
      // PHASE_4 / B7 scheduling — the free/busy availability finder
      // (Müsaitlik; POST /v1/scheduling/availability — an employee_self caller
      // may probe only their own id, a colleague's id 403s) at the index, and
      // the self-scheduling links surface (Planlama Bağlantıları) at /links.
      {
        path: "me/scheduling",
        ...lazyRoute(() => import("@/features/calendar"), (m) => m.AvailabilityContent),
      },
      {
        path: "me/scheduling/links",
        ...lazyRoute(() => import("@/features/calendar"), (m) => m.SchedulingLinksContent),
      },
      // PHASE_4 / B2 notifications — the employee's OWN notification preference
      // settings (Bildirim Ayarları, notif-settings, employee_self; server-
      // pinned to own preferences). Per notification-type channel toggles +
      // frequency + quiet-hours, PUT-on-save per type. The notification INBOX
      // (the TopBar bell) is a separate, pre-existing surface.
      {
        path: "me/notifications",
        ...lazyRoute(
          () => import("@/features/notifications"),
          (m) => m.NotificationSettingsContent,
        ),
      },
      // SEM-6835 — "Kimlik yöntemlerim": the caller's own passkeys + IdP links.
      // Self-service, no capability gate — the endpoint behind it is requireAuth
      // only, because every employee reads their own row (SEM-6833).
      {
        path: "me/security",
        ...lazyRoute(
          () => import("@/features/security"),
          (m) => m.IdentityMethodsContent,
        ),
      },
      // SEM-6363 — the notification INBOX itself (employee_self), the full-page
      // twin of the TopBar bell's popover. Deliberately NOT under me/ despite
      // being self-scoped: the bell's "See all" is the only way in, and a
      // /me/notifications that means SETTINGS while /me/notifications/inbox
      // means MAIL is a distinction nobody reads correctly in a URL bar.
      {
        path: "notifications",
        ...lazyRoute(
          () => import("@/features/notifications"),
          (m) => m.NotificationInboxContent,
        ),
      },
      // Çalışanın bordro sayfası (KVKK gereği yalnız PDF): /me/payroll en yeni
      // dönemi, /me/payroll/:runId bağlantıdaki dönemi aynı PayslipContent ile
      // açar; kapsamı sunucu çağırana daraltır (employee_self).
      { path: "me/payroll", Component: PayslipContent },
      { path: "me/buddies", ...lazyRoute(() => import("@/features/onboarding/buddy-content"), (m) => m.BuddyHomeContent) },
      { path: "me/compensation", ...lazyRoute(() => import("@/features/self-payroll-history"), (m) => m.OwnCompensationPage) },
      { path: "me/onboarding/tasks/:taskId/form", ...lazyRoute(() => import("@/features/onboarding/task-form-content"), (m) => m.OnboardingTaskFormContent) },
      { path: "me/forms", ...lazyRoute(() => import("@/features/forms/pages"), (m) => m.MyFormsPage) },
      { path: "me/forms/:appId", ...lazyRoute(() => import("@/features/forms/pages"), (m) => m.MyFormPage) },
      { path: "forms", ...lazyRoute(() => import("@/features/forms/pages"), (m) => m.FormsManagementPage) },
      { path: "forms/new", ...lazyRoute(() => import("@/features/forms/pages"), (m) => m.NewFormPage) },
      { path: "forms/:appId", ...lazyRoute(() => import("@/features/forms/pages"), (m) => m.FormManagementPage) },
      { path: "me/cafeteria", ...lazyRoute(() => import("@/features/cafeteria/pages"), (m) => m.MyCafeteriaPage) },
      { path: "cafeteria", ...lazyRoute(() => import("@/features/cafeteria/pages"), (m) => m.CafeteriaManagementPage) },
      { path: "cafeteria/new", ...lazyRoute(() => import("@/features/cafeteria/pages"), (m) => m.NewCafeteriaServicePage) },
      { path: "cafeteria/:serviceId", ...lazyRoute(() => import("@/features/cafeteria/pages"), (m) => m.CafeteriaServicePage) },
      { path: "me/vehicle-requests", ...lazyRoute(() => import("@/features/vehicle-requests"), (m) => m.VehicleRequestSelfPage) },
      { path: "me/vehicle-requests/:id", ...lazyRoute(() => import("@/features/vehicle-requests"), (m) => m.VehicleRequestSelfDetailPage) },
      { path: "vehicle-requests", ...lazyRoute(() => import("@/features/vehicle-requests"), (m) => m.VehicleRequestAdminPage) },
      { path: "vehicle-requests/:id", ...lazyRoute(() => import("@/features/vehicle-requests"), (m) => m.VehicleRequestAdminDetailPage) },
      { path: "me/document-requests", ...lazyRoute(() => import("@/features/document-requests"), (m) => m.DocumentRequestSelfPage) },
      { path: "me/document-requests/:id", ...lazyRoute(() => import("@/features/document-requests"), (m) => m.DocumentRequestSelfDetailPage) },
      { path: "document-requests", ...lazyRoute(() => import("@/features/document-requests"), (m) => m.DocumentRequestAdminPage) },
      { path: "document-requests/:id", ...lazyRoute(() => import("@/features/document-requests"), (m) => m.DocumentRequestAdminDetailPage) },
      { path: "me/signature-requests", ...lazyRoute(() => import("@/features/signature-requests"), (m) => m.SignatureRequestSelfPage) },
      { path: "me/signature-requests/:id", ...lazyRoute(() => import("@/features/signature-requests"), (m) => m.SignatureRequestSelfDetailPage) },
      { path: "signature-requests", ...lazyRoute(() => import("@/features/signature-requests"), (m) => m.SignatureRequestAdminPage) },
      { path: "signature-requests/new", ...lazyRoute(() => import("@/features/signature-requests"), (m) => m.NewSignatureRequestPage) },
      { path: "signature-requests/:id", ...lazyRoute(() => import("@/features/signature-requests"), (m) => m.SignatureRequestAdminDetailPage) },
      { path: "me/policies", ...lazyRoute(() => import("@/features/policies/pages"), (m) => m.MyPoliciesPage) },
      { path: "me/policies/:policyId", ...lazyRoute(() => import("@/features/policies/pages"), (m) => m.MyPolicyPage) },
      { path: "policies", ...lazyRoute(() => import("@/features/policies/pages"), (m) => m.PoliciesManagementPage) },
      { path: "policies/new", ...lazyRoute(() => import("@/features/policies/pages"), (m) => m.NewPolicyPage) },
      { path: "policies/:policyId", ...lazyRoute(() => import("@/features/policies/pages"), (m) => m.PolicyManagementPage) },
      { path: "me/expenses", ...lazyRoute(() => import("@/features/expenses"), (m) => m.ExpenseSelfPage) },
      { path: "me/expenses/:id", ...lazyRoute(() => import("@/features/expenses"), (m) => m.ExpenseSelfDetailPage) },
      { path: "expenses", ...lazyRoute(() => import("@/features/expenses"), (m) => m.ExpenseAdminPage) },
      { path: "expenses/:id", ...lazyRoute(() => import("@/features/expenses"), (m) => m.ExpenseAdminDetailPage) },
      { path: "me/advances", ...lazyRoute(() => import("@/features/payroll-advances"), (m) => m.AdvanceSelfPage) },
      { path: "me/advances/new", ...lazyRoute(() => import("@/features/payroll-advances"), (m) => m.AdvanceRequestPage) },
      { path: "me/garnishments", ...lazyRoute(() => import("@/features/self-payroll-history"), (m) => m.OwnGarnishmentsPage) },
      { path: "me/payroll/:runId", Component: PayslipContent },
      // FP1 — Takdir & Kudos (me-kudos, self context). The SAME KudosContent as
      // the admin /kudos twin (ADR-014 §2 no-copy); the employee surface adds the
      // Aldığım/Verdim tabs (route-detected via pathname). The server visibility-
      // scopes the feed per caller (public + to/from-them); the moderate action
      // stays moderator-role-only server-side regardless.
      {
        path: "me/kudos",
        ...lazyRoute(() => import("@/features/recognition"), (m) => m.KudosContent),
      },
      // FP2b/FU3 — Anketlerim (me-surveys, self context). The employee surface
      // calls GET /v1/ee/me/surveys for its own OPEN, cohort-eligible rows. The
      // server resolves active supergroup membership; we never call the admin list
      // as an employee. The respond form lives at /me/surveys/:id (RespondView),
      // reachable through each eligible survey's own Respond link.
      //
      // SEM-7199: this used to mount SurveysContent, which read the pathname to
      // decide WHICH surface to render. Two routes now name their own component,
      // so the branch is gone — the router already knows which surface it is.
      // ADR-014 §2 (no-copy) still holds: the two surfaces are different screens
      // reading different endpoints, never a forked copy of one screen.
      { path: "me/training", ...lazyRoute(() => import("@/features/learning/learner-pages"), (m) => m.MyLearningPage) },
      { path: "me/training/paths/:id", ...lazyRoute(() => import("@/features/learning/path-assignment-pages"), (m) => m.MyLearningPathPage) },
      { path: "me/training/:id", ...lazyRoute(() => import("@/features/learning/learner-pages"), (m) => m.LearningPlayerPage) },
      { path: "me/announcements", ...lazyRoute(() => import("@/features/announcements"), (m) => m.MyAnnouncementsPage) },
      { path: "me/announcements/:id", ...lazyRoute(() => import("@/features/announcements"), (m) => m.MyAnnouncementDetailPage) },
      {
        path: "me/surveys",
        ...lazyRoute(() => import("@/features/surveys"), (m) => m.EmployeeSurveysIndex),
      },
      {
        path: "me/surveys/:id",
        ...lazyRoute(() => import("@/features/surveys"), (m) => m.RespondView),
      },
      // P9/S7 — scr_support_my_tickets (Destek Taleplerim). The server
      // row-narrows a non-agent caller to requester_id = self; the requester
      // reply/reopen flow renders the SAME TicketDetailContent as the agent
      // /support/tickets/:ticketId twin (ADR-014 §2 no-copy — the server
      // scopes the timeline per viewer: internal rows never reach a requester).
      {
        path: "me/support",
        ...lazyRoute(() => import("@/features/support-tickets"), (m) => m.MyTicketsContent),
      },
      {
        path: "me/support/:ticketId",
        ...lazyRoute(() => import("@/features/support-tickets"), (m) => m.TicketDetailContent),
      },
    ],
  },

  // /dashboard was the admin-hat twin of "/" (same DashboardPage; only the
  // shell's menu differed). With the hat gone the twin is redundant — keep an
  // explicit redirect because the C1-era hat switch + demo docs linked here.
  { path: "/dashboard", element: <Navigate to="/" replace /> },

  // P9/S7 Help Center (spec 70 scr_support_help_center +
  // scr_support_article_editor). The Help Center is the universal KB reading
  // surface (every employee has perm_support_article_read); the editor routes
  // render an honest no-permission state for non-manage callers (the server
  // enforces perm_support_article_manage on every write regardless).
  {
    path: "/help",
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      {
        index: true,
        ...lazyRoute(() => import("@/features/support-help"), (m) => m.HelpCenterContent),
      },
      {
        path: "articles/new",
        ...lazyRoute(() => import("@/features/support-help"), (m) => m.ArticleEditorContent),
      },
      {
        path: "articles/:articleId/edit",
        ...lazyRoute(() => import("@/features/support-help"), (m) => m.ArticleEditorContent),
      },
    ],
  },

  // Tenant-scope — P9/S7 agent ticket inbox + detail (spec 70
  // scr_support_ticket_inbox + scr_support_ticket_detail). The agent boundary
  // is SERVER-side (SUPPORT_AGENT_ROLES in-handler); a non-agent opening this
  // route sees only their own rows + an honest notice. The SAME
  // TicketDetailContent renders the requester view reached from /me/support.
  {
    path: "/support/tickets",
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      {
        index: true,
        ...lazyRoute(() => import("@/features/support-tickets"), (m) => m.TicketInboxContent),
      },
      {
        path: ":ticketId",
        ...lazyRoute(() => import("@/features/support-tickets"), (m) => m.TicketDetailContent),
      },
    ],
  },

  // Tenant-scope — /people renders the SAME EmployeeListContent as
  // /me/directory (ADR-014 §2 no-copy proof); the server scopes rows.
  {
    path: "/people",
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      // The personnel list and its write surfaces are the /people rail entry and
      // open only for a caller the rail shows it to (E38). A pathless layout, so
      // the paths below do not change.
      {
        element: <RequireMenuEntry />,
        children: [
          { index: true, Component: EmployeeListContent },
          // Personnel add/edit — the SAME EmployeeFormPage wires POST
          // /v1/employees (create) and PATCH /v1/employees/:id (edit); the `:id`
          // param selects the mode. Manager+ scope server-side.
          { path: "create", Component: EmployeeFormPage },
          { path: ":id/edit", Component: EmployeeFormPage },
        ],
      },
      // The person's READ address (SEM-6349), now a PAGE of its own
      // (SEM-6354) rather than a drawer over the list: a page can be shared,
      // opened in a new tab, closed with the back button, and can carry links
      // to the next person. Static `create` outranks this dynamic segment, and
      // `:id/edit` is the more specific match, so neither is shadowed.
      //
      // Why a read route had to exist at all: `:id/edit` was the ONLY person
      // address, and it is a manager-scoped WRITE surface. Linking a name there
      // turns "who is this?" into a permission question.
      //
      // Deliberately OUTSIDE the entry guard: an employee reaches a person card
      // from a mention, the org chart or an approval, and the page renders the
      // card for them (E41). Guarding it would break that.
      { path: ":id", Component: EmployeeDetailContent },
    ],
  },

  // Tenant-scope — the leave admin queue. Renders the SAME LeaveListContent as
  // /me/leave; only the resolved scope (server-side) differs. /leave/balances
  // is the admin balance view (same LeaveBalanceContent as /me/leave/balance).
  {
    path: "/leave",
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <LeaveListContent view="management" /> },
      { path: "balances", Component: LeaveBalanceContent },
      // Faz 8.6 / A3 — the SAME LeaveCalendarContent as /me/leave/calendar
      // (ADR-014 §2 no-copy); the server scopes the rows per caller.
      { path: "calendar", Component: LeaveCalendarContent },
    ],
  },

  // Tenant-scope — P5/B4 the requests/approvals queue (Talepler). Renders the SAME
  // ApprovalListContent as /me/requests; only the server-side narrow differs —
  // the manager acts on SUBORDINATE requests of every approval-backed kind here
  // (ADR-014 §2 no-copy). Self-approval is denied server-side regardless
  // (dual-role.ts self-exclusion).
  {
    path: "/requests",
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, ...lazyRoute(() => import("@/features/approvals"), (m) => m.RequestQueueContent) },
    ],
  },

  // Spec 06 generic approval detail. Notification delivery already deep-links
  // assigned approvers to /approvals/:id; this route is deliberately generic
  // so domain producers (including payroll maker-checker) never fake a local
  // approve transition. The server remains the authorization boundary.
  {
    path: "/approvals",
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      // Without an index the bare /approvals rendered the shell around an EMPTY
      // outlet — nav present, content area blank. Reachable by trimming the id
      // off a notification deep-link. There is no separate approvals list to
      // show: /requests IS the decision inbox, so send the caller there.
      { index: true, element: <Navigate to="/requests" replace /> },
      // Static segment before the param so "history" is never read as an id.
      {
        path: "history",
        ...lazyRoute(() => import("@/features/approvals"), (m) => m.ApprovalHistoryContent),
      },
      {
        path: ":approvalId",
        ...lazyRoute(() => import("@/features/approvals"), (m) => m.ApprovalDecisionContent),
      },
    ],
  },

  // Tenant-scope — PDKS (time/attendance) timesheet queue (B2). Twin of
  // /me/attendance; the manager queue renders AdminPdksContent.
  {
    path: "/pdks",
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      {
        element: <RequireMenuEntry />,
        children: [{ index: true, Component: AdminPdksContent }],
      },
    ],
  },

  // Tenant-scope — organization (B3). SAME OrgContent as the read-only
  // /me/org twin; the server scopes data + denies mutations.
  //
  // `scenarios/:scenarioId` is the target of the org-scenario deep links raised
  // by halt tasks and approval routing (services/platform-graph .../org-scenario-
  // apply.ts `orgScenarioDeepLink`). Those links said `/org-chart/scenarios/:id`
  // — a root this router has never mounted — so every one of them hit the
  // catch-all and redirected to `/`. `/org` is the only org root; the deep-link
  // producer now derives from it.
  //
  // The LIST is a tab of /org, not a page: `scenarios` redirects rather than
  // rendering, so one screen has one URL and the org tab strip stays on screen.
  //
  // The DETAIL route carries RequireScenarioAccess on top of RequireAuth: the
  // tab is offered only to callers holding `hris.edit`, and a typed URL must not
  // be a way around a decision the tab strip already made. See that file for why
  // this is consistency and NOT the security fix — the server-side scenario read
  // path has no row-level scope, which is its own (filed) work item.
  {
    path: "/org",
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, Component: OrgContent },
      { path: "scenarios", element: <Navigate to={ORG_SCENARIOS_PATH} replace /> },
      {
        path: "scenarios/:scenarioId",
        element: <RequireScenarioAccess />,
        children: [
          {
            index: true,
            ...lazyRoute(
              () => import("@/features/org-scenarios"),
              (m) => m.OrgScenarioDetailContent,
            ),
          },
        ],
      },
    ],
  },

  // Tenant-scope — Envanter & Zimmet (PHASE_3 / B1). AdminInventoryContent is the
  // admin asset-inventory surface (list → register → assign → return →
  // write-off); the employee Zimmetlerim twin renders MyAssetsContent at
  // /me/assets. The server scopes data + denies out-of-scope mutations.
  {
    path: "/assets",
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      // SEM-6954: yolsuz bir düzen katmanı — sekme çubuğunu (AssetsShellContent)
      // çocuklarına çizer ve adresi değiştirmez, böylece /assets index olarak
      // kalır. Katalog yan menüde kendi girdisi olduğu için şeridin dışındadır.
      {
        path: "catalog",
        ...lazyRoute(() => import("@/features/assets"), (m) => m.CatalogContent),
      },
      {
        ...lazyRoute(() => import("@/features/assets"), (m) => m.AssetsShellContent),
        children: [
          {
            index: true,
            ...lazyRoute(() => import("@/features/assets"), (m) => m.AdminInventoryContent),
          },
          {
            path: "stock",
            ...lazyRoute(() => import("@/features/assets"), (m) => m.StockContent),
          },
          {
            path: "assignments",
            ...lazyRoute(() => import("@/features/assets"), (m) => m.AssignmentHistoryContent),
          },
          {
            element: <RequireMenuEntry />,
            children: [{
              path: "audits",
              ...lazyRoute(() => import("@/features/assets/audit-plans-content"), (m) => m.AssetAuditPlansContent),
            }],
          },
        ],
      },
      {
        element: <RequireMenuEntry />,
        children: [{
          path: "audits/:cycleId",
          ...lazyRoute(() => import("@/features/assets/audit-detail-content"), (m) => m.AssetAuditDetailContent),
        }],
      },
      // SEM-6955: form ve detay sekme çubuğunun KARDEŞİDİR, çocuğu değil.
      // AssetsShellContent'in başlığı rotası olmayan sekme çizmeyi yasaklıyor
      // ve index sekmesi `end: true` taşıyor — bu rotaları düzenin altına
      // koymak, hiçbir sekmenin aktif olmadığı bir sekme çubuğu çizerdi.
      // Sabit `new`, kardeşi olan dinamik parçadan ÖNCE bildirilir.
      {
        path: "new",
        ...lazyRoute(() => import("@/features/assets"), (m) => m.AssetFormContent),
      },
      {
        path: ":assetId",
        ...lazyRoute(() => import("@/features/assets"), (m) => m.AssetDetailPage),
      },
      {
        path: ":assetId/edit",
        ...lazyRoute(() => import("@/features/assets"), (m) => m.AssetFormContent),
      },
    ],
  },

  // Tenant-scope — Yan Haklar (PHASE_3 / B2). The admin surface is three panels:
  // plan catalog (create → publish → archive → eligibility-preview), tenant
  // enrollments (→ terminate) and carrier registration. The employee Yan
  // Haklarım twin renders MyBenefitsContent at /me/benefits. The server scopes
  // data + denies out-of-scope mutations.
  {
    path: "/benefits",
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      {
        // Yönetim yüzeyi: çalışanın kendi karşılığı /me/benefits (E38).
        element: <RequireMenuEntry />,
        children: [
          // SEM-7197: yolsuz bir düzen katmanı — sekme çubuğunu (BenefitsShellContent)
          // üç çocuğa da çizer ve adresi değiştirmez, böylece /benefits index olarak
          // kalır. Paneller `?tab=` yerine kendi adreslerini taşır; üst şerit onları
          // TEK sekmede tutar (`components/shell-tabs.ts`).
          {
            ...lazyRoute(() => import("@/features/benefits"), (m) => m.BenefitsShellContent),
            children: [
              {
                index: true,
                ...lazyRoute(() => import("@/features/benefits"), (m) => m.BenefitsPlansContent),
              },
              {
                path: "enrollments",
                ...lazyRoute(() => import("@/features/benefits"), (m) => m.BenefitsEnrollmentsContent),
              },
              {
                path: "carriers",
                ...lazyRoute(() => import("@/features/benefits"), (m) => m.BenefitsCarriersContent),
              },
            ],
          },
        ],
      },
    ],
  },

  // Tenant-scope — Görevler (PHASE_4 / B1). TasksContent is the broader task inbox
  // (the server scopes the rows by the caller's tier); the employee Görevlerim
  // twin renders MyTasksContent at /me/tasks. The lifecycle actions stay
  // assignee-only server-side regardless (a non-assignee action 403s).
  {
    path: "/tasks",
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [{ index: true, Component: TasksContent }],
  },

  // 16a — the tier-scoped event list (GET /v1/calendar-events without `mine`).
  {
    path: "/events",
    element: <RequireAuth><AppShell /></RequireAuth>,
    children: [
      { element: <RequireMenuEntry />, children: [
        { index: true, ...lazyRoute(() => import("@/features/calendar/events-admin-content"), (m) => m.EventsAdminContent) },
      ] },
    ],
  },

  // Tenant-scope — the onboarding tracking hub (B5): running onboardings, the
  // closed ones and the plan-template catalog. /me/onboarding renders the new
  // hire's own record (OnboardingContent).
  {
    path: "/onboarding",
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      // SEM-7199: yolsuz bir düzen katmanı — sekme çubuğunu (OnboardingShellContent)
      // üç çocuğa da çizer ve adresi değiştirmez, böylece /onboarding index olarak
      // kalır. Paneller `?tab=` yerine kendi adreslerini taşır; üst şerit onları
      // TEK sekmede tutar (`components/shell-tabs.ts`).
      //
      // Kayıt ve form ekranları bu katmanın KARDEŞİDİR, çocuğu değil: şeridi
      // taşımazlar ve kendi üst sekmelerini açarlar.
      {
        ...lazyRoute(() => import("@/features/onboarding"), (m) => m.OnboardingShellContent),
        children: [
          {
            index: true,
            ...lazyRoute(() => import("@/features/onboarding"), (m) => m.OnboardingInFlightContent),
          },
          {
            path: "terminal",
            ...lazyRoute(() => import("@/features/onboarding"), (m) => m.OnboardingTerminalContent),
          },
          {
            path: "plans",
            ...lazyRoute(() => import("@/features/onboarding"), (m) => m.OnboardingPlansPanel),
          },
        ],
      },
      {
        path: "instances/:instanceId",
        ...lazyRoute(
          () => import("@/features/onboarding"),
          (m) => m.OnboardingInstanceDetailContent,
        ),
      },
      // "new" before ":planId" so the literal wins — otherwise the editor would
      // try to LOAD a plan whose id is the string "new".
      {
        path: "plans/new",
        ...lazyRoute(() => import("@/features/onboarding"), (m) => m.PlanFormPage),
      },
      {
        path: "plans/:planId",
        ...lazyRoute(() => import("@/features/onboarding"), (m) => m.PlanFormPage),
      },
    ],
  },

  {
    path: "/offboarding",
    element: <RequireAuth><AppShell /></RequireAuth>,
    children: [
      { element: <RequireMenuEntry />, children: [
        { index: true, ...lazyRoute(() => import("@/features/offboarding"), (m) => m.OffboardingHubContent) },
        { path: "templates", ...lazyRoute(() => import("@/features/offboarding"), (m) => m.OffboardingTemplatesContent) },
        { path: "templates/new", ...lazyRoute(() => import("@/features/offboarding"), (m) => m.OffboardingTemplateEditorContent) },
        { path: "templates/:templateId", ...lazyRoute(() => import("@/features/offboarding"), (m) => m.OffboardingTemplateEditorContent) },
        { path: "instances/new", ...lazyRoute(() => import("@/features/offboarding"), (m) => m.OffboardingStartContent) },
        { path: "instances/:instanceId", ...lazyRoute(() => import("@/features/offboarding"), (m) => m.OffboardingInstanceContent) },
      ] },
    ],
  },

  // Tenant-scope — PHASE_2 / B5 TR bordro. The BulkPayrollWizard (create →
  // calculate → submit → generic decision → finalize), the admin payslip-preview (per-statement
  // line items — the ONLY line-item surface; employees get PDF-only), and the
  // exit/severance screen. Every write route is executive_broad server-side.
  {
    path: "/payroll",
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      // Read, authoring and exit screens have distinct server permissions.
      // The longest matching menu entry supplies the same gate for typed URLs.
      {
        element: <RequireMenuEntry />,
        children: [
          {
            index: true,
            ...lazyRoute(() => import("@/features/payroll"), (m) => m.AdminPayrollContent),
          },
          {
            path: ":runId",
            ...lazyRoute(() => import("@/features/payroll"), (m) => m.AdminPayrollContent),
          },
          {
            path: "exit",
            ...lazyRoute(() => import("@/features/payroll"), (m) => m.ExitPayrollContent),
          },
          // A6 master data. Under /payroll rather than the Settings tab bar: those
          // tabs are settings.read-gated, these two are payroll-gated, and a tab
          // that 403s on click is worse than no tab. (Static segments outrank the
          // sibling ":runId" by React Router's ranking, not by array order — the
          // existing "exit" child already relies on that.)
          { path: "advances", ...lazyRoute(() => import("@/features/payroll-advances"), (m) => m.AdvanceAdminPage) },
          { path: "advances/policy/:companyId/new", ...lazyRoute(() => import("@/features/payroll-advances"), (m) => m.AdvancePolicyPage) },
          { path: "advances/:advanceId/payment", ...lazyRoute(() => import("@/features/payroll-advances"), (m) => m.AdvancePaymentPage) },
          {
            path: "workplaces",
            ...lazyRoute(() => import("@/features/payroll"), (m) => m.WorkplacesContent),
          },
          {
            path: "employees",
            ...lazyRoute(() => import("@/features/payroll"), (m) => m.EmployeeMasterDataContent),
          },
          // D8 — İcra/Nafaka. Same payroll gate, same reason for living here: the
          // court orders it lists are payroll master data, not settings.
          {
            path: "garnishments",
            ...lazyRoute(() => import("@/features/payroll"), (m) => m.GarnishmentsContent),
          },
          {
            path: "compensation",
            ...lazyRoute(() => import("@/features/compensation-changes"), (m) => m.CompensationChangesPage),
          },
          {
            path: "compensation/new",
            ...lazyRoute(() => import("@/features/compensation-changes"), (m) => m.CompensationProposalPage),
          },
          {
            path: "compensation/:changeId",
            ...lazyRoute(() => import("@/features/compensation-changes"), (m) => m.CompensationChangeDetailPage),
          },
          {
            path: ":runId/preview/:sid",
            ...lazyRoute(() => import("@/features/payroll"), (m) => m.PayslipPreviewContent),
          },
        ],
      },
    ],
  },

  // System / Ayarlar. Complementary children mount here:
  //  - SettingsContent (P5/B3) is now a LAYOUT (SEM-5308): PageHeader + the
  //    ROUTE-DRIVEN tab bar + <Outlet/>. Each tab is a real route — index
  //    (Kurum Bilgileri → CompanyProfileContent) and branding (Marka →
  //    BrandingContent); a tab with no built route is NOT rendered (no ghost
  //    tabs). Future settings surfaces (users/audit-log — C4/C6) add a route
  //    child + a tab entry together.
  //  - roles (P5/B2): RolesPermissionsContent, the rebuilt roles-permissions
  //    matrix SPA — full-page sibling, deliberately OUTSIDE the tab layout.
  //    Server-gated settings.read; a non-admin caller's
  //    GET /v1/permission-profiles 403s, surfaced as the "no permission" state.
  {
    path: "/settings",
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      // Every settings page belongs to one rail entry: the hub for the tab
      // pages, its own row for the standalone catalogues. RequireMenuEntry finds
      // the entry from the URL against the manifest, so this guard carries no
      // capability / tier pair of its own (E38).
      {
        element: <RequireMenuEntry />,
        children: [
          { path: "home", ...lazyRoute(() => import("@/features/admin-setup/admin-home"), (m) => m.AdminHomePage) },
          { path: "setup", ...lazyRoute(() => import("@/features/admin-setup/setup-wizard"), (m) => m.SetupWizardPage) },
          {
            ...lazyRoute(() => import("@/features/admin-settings"), (m) => m.SettingsContent),
            children: [
              { path: "feature-flags", ...lazyRoute(() => import("@/features/admin-flags/tenant-flags-page"), (m) => m.TenantFlagsPage) },
              { path: "ai", ...lazyRoute(() => import("@/features/admin-ai/tenant-ai-page"), (m) => m.TenantAiPage) },
              { path: "data-export", ...lazyRoute(() => import("@/features/admin-data-export/tenant-data-export-page"), (m) => m.TenantDataExportPage) },
              { path: "matrix-approval-policy", ...lazyRoute(() => import("@/features/admin-matrix-policy/matrix-policy-page"), (m) => m.MatrixPolicyPage) },
              { path: "environments", ...lazyRoute(() => import("@/features/admin-environments/environments-page"), (m) => m.EnvironmentsPage) },
              {
                index: true,
                ...lazyRoute(
                  () => import("@/features/admin-settings"),
                  (m) => m.CompanyProfileContent,
                ),
              },
              {
                path: "branding",
                ...lazyRoute(
                  () => import("@/features/admin-settings"),
                  (m) => m.BrandingContent,
                ),
              },
              {
                // SEM-6040. "Kurum Bilgileri" (index) holding TERCİHLERİdir;
                // burası holdingin işlettiği tüzel kişiliklerin kaydıdır. İkisi
                // komşu sekme, çünkü üçüncü bir şirket yüzeyi istemiyoruz.
                path: "companies",
                ...lazyRoute(
                  () => import("@/features/admin-settings"),
                  (m) => m.CompaniesContent,
                ),
              },
              {
                path: "users",
                ...lazyRoute(
                  () => import("@/features/admin-settings"),
                  (m) => m.UsersContent,
                ),
              },
              {
                path: "sso",
                ...lazyRoute(
                  () => import("@/features/admin-settings"),
                  (m) => m.SsoContent,
                ),
              },
              {
                path: "integrations",
                ...lazyRoute(
                  () => import("@/features/admin-settings"),
                  (m) => m.IntegrationsContent,
                ),
              },
              {
                // The tenant-wide defaults. The employee's OWN preferences live at
                // /me/notifications above and always win over what is set here.
                path: "notifications",
                ...lazyRoute(
                  () => import("@/features/admin-settings"),
                  (m) => m.NotificationsContent,
                ),
              },
              {
                path: "audit-log",
                ...lazyRoute(
                  () => import("@/features/admin-settings"),
                  (m) => m.AuditLogContent,
                ),
              },
              {
                // SEM-5549 — the tenant's own billing view (spec 42
                // scr_admin_billing over the spec-64 /v1/billing/* read routes).
                // A settings tab, not a rail entry: it is tenant administration
                // config like every other tab here, and the backend gates it on
                // the same settings.read cell.
                path: "billing",
                ...lazyRoute(() => import("@/features/billing"), (m) => m.BillingContent),
              },
            ],
          },
          {
            path: "roles",
            ...lazyRoute(
              () => import("@/features/roles-permissions"),
              (m) => m.RolesPermissionsContent,
            ),
          },
          // W4b — the approval chain-builder admin screen (spec 06
          // scr_approval_chain_builder). List + create/edit form over
          // /v1/approval-chain-templates; server-gated approvals.read for the
          // list, perm_approvals_admin (in-handler) for every write.
          {
            path: "approval-chains",
            ...lazyRoute(
              () => import("@/features/approval-chains"),
              (m) => m.ApprovalChainsContent,
            ),
          },
          {
            path: "approval-chains/new",
            ...lazyRoute(
              () => import("@/features/approval-chains"),
              (m) => m.ApprovalChainFormPage,
            ),
          },
          {
            path: "approval-chains/:id/edit",
            ...lazyRoute(
              () => import("@/features/approval-chains"),
              (m) => m.ApprovalChainFormPage,
            ),
          },
          // SEM-7202 (P1c) — Leave type catalogue & accrual rules.
          {
            path: "leave-types",
            ...lazyRoute(
              () => import("@/features/leave/leave-types-page"),
              (m) => m.LeaveTypesPage,
            ),
          },
          // SEM-7208 (T2) — Cost centre catalogue (entity_cost_center).
          {
            path: "cost-centers",
            ...lazyRoute(
              () => import("@/features/cost-centers"),
              (m) => m.CostCentersPage,
            ),
          },
          // SEM-7203 (P2) — Department structure & cost centers catalogue.
          {
            path: "departments",
            ...lazyRoute(
              () => import("@/features/departments"),
              (m) => m.DepartmentsPage,
            ),
          },
          // SEM-7209 (T3) — ünvan ve kademe kataloğu.
          {
            path: "job-catalogue",
            ...lazyRoute(
              () => import("@/features/job-catalogue"),
              (m) => m.JobCataloguePage,
            ),
          },
          // SEM-7204 (P3) — Locations catalogue & geofence definitions.
          {
            path: "locations",
            ...lazyRoute(
              () => import("@/features/locations"),
              (m) => m.LocationsPage,
            ),
          },
          // Damga cihazları envanteri ve kiosk eşleştirme (spec 37).
          {
            path: "devices",
            ...lazyRoute(
              () => import("@/features/devices"),
              (m) => m.DevicesPage,
            ),
          },

          // SEM-7207 — resmî tatil takvimi (kanuni satırlar + kuruma özel günler).
          {
            path: "holidays",
            ...lazyRoute(
              () => import("@/features/holidays"),
              (m) => m.HolidaysPage,
            ),
          },

          // SEM-7206 — vardiya çizelgesi tanımı, vardiya satırları ve yayımlama.
          {
            path: "schedules",
            ...lazyRoute(
              () => import("@/features/time/schedules-page"),
              (m) => m.SchedulesPage,
            ),
          },

          // SEM-7205 (P4) — Org dimension catalogue (scr_admin_org_dimensions).
          {
            path: "org-dimensions",
            ...lazyRoute(
              () => import("@/features/org-dimensions"),
              (m) => m.OrgDimensionsPage,
            ),
          },
        ],
      },
    ],
  },

  // Tenant-scope — FP1 Takdir & Kudos. Renders the SAME KudosContent as the
  // employee /me/kudos twin (ADR-014 §2 no-copy); the admin surface omits the
  // self received/given tabs and exposes the moderate affordance to moderator
  // roles (the rail hides /kudos for a caller without recognition.read; the
  // moderate call 403s for a non-moderator regardless — server is the authority).
  {
    path: "/kudos",
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      {
        index: true,
        ...lazyRoute(() => import("@/features/recognition"), (m) => m.KudosContent),
      },
    ],
  },

  {
    path: "/learning",
    element: <RequireAuth><AppShell /></RequireAuth>,
    children: [
      { path: "admin/renewals", ...lazyRoute(() => import("@/features/learning/renewal-pages"), (m) => m.LearningRenewalsPage) },
      { path: "admin/renewals/:id", ...lazyRoute(() => import("@/features/learning/renewal-pages"), (m) => m.LearningRenewalPage) },
      { path: "admin/rules", ...lazyRoute(() => import("@/features/learning/rule-pages"), (m) => m.LearningRulesPage) },
      { path: "admin/rules/new", ...lazyRoute(() => import("@/features/learning/rule-pages"), (m) => m.LearningRulePage) },
      { path: "admin/rules/:id", ...lazyRoute(() => import("@/features/learning/rule-pages"), (m) => m.LearningRulePage) },
      { path: "certificates/:id", ...lazyRoute(() => import("@/features/learning/certificate-pages"), (m) => m.LearningCertificatePage) },
      { path: "reports", ...lazyRoute(() => import("@/features/learning/report-pages"), (m) => m.LearningReportsPage) },
      { path: "admin/requirements", ...lazyRoute(() => import("@/features/learning/compliance-pages"), (m) => m.LearningRequirementsPage) },
      { path: "external-records", ...lazyRoute(() => import("@/features/learning/compliance-pages"), (m) => m.LearningExternalRecordsPage) },
      { path: "paths", ...lazyRoute(() => import("@/features/learning/path-author-pages"), (m) => m.LearningPathsPage) },
      { path: "paths/new", ...lazyRoute(() => import("@/features/learning/path-author-pages"), (m) => m.LearningPathEditorPage) },
      { path: "paths/:id", ...lazyRoute(() => import("@/features/learning/path-author-pages"), (m) => m.LearningPathPage) },
      { path: "paths/:id/edit", ...lazyRoute(() => import("@/features/learning/path-author-pages"), (m) => m.LearningPathEditorPage) },
      { path: "path-assignments/new", ...lazyRoute(() => import("@/features/learning/path-assignment-pages"), (m) => m.LearningPathAssignPage) },
      { path: "path-assignments/:id", ...lazyRoute(() => import("@/features/learning/path-assignment-pages"), (m) => m.LearningPathAssignmentPage) },
      { index: true, ...lazyRoute(() => import("@/features/learning/author-pages"), (m) => m.LearningCatalogPage) },
      { path: "new", ...lazyRoute(() => import("@/features/learning/author-pages"), (m) => m.LearningEditorPage) },
      { path: ":id/edit", ...lazyRoute(() => import("@/features/learning/author-pages"), (m) => m.LearningEditorPage) },
      { path: ":id/preview", ...lazyRoute(() => import("@/features/learning/author-pages"), (m) => m.LearningPreviewPage) },
      { path: ":id/translate", ...lazyRoute(() => import("@/features/learning/author-pages"), (m) => m.LearningTranslatePage) },
      { path: ":id", ...lazyRoute(() => import("@/features/learning/author-pages"), (m) => m.LearningCoursePage) },
      { path: "assignments", ...lazyRoute(() => import("@/features/learning/assignment-pages"), (m) => m.LearningAssignmentsPage) },
      { path: "assignments/new", ...lazyRoute(() => import("@/features/learning/assignment-pages"), (m) => m.LearningAssignPage) },
      { path: "assignments/:id", ...lazyRoute(() => import("@/features/learning/assignment-pages"), (m) => m.LearningAssignmentPage) },
    ],
  },

  {
    path: "/announcements",
    element: <RequireAuth><AppShell /></RequireAuth>,
    children: [
      { index: true, ...lazyRoute(() => import("@/features/announcements"), (m) => m.AnnouncementsPage) },
      { path: "new", ...lazyRoute(() => import("@/features/announcements"), (m) => m.AnnouncementEditorPage) },
      { path: "moderation", ...lazyRoute(() => import("@/features/announcements"), (m) => m.AnnouncementModerationPage) },
      { path: ":id/edit", ...lazyRoute(() => import("@/features/announcements"), (m) => m.AnnouncementEditorPage) },
      { path: ":id", ...lazyRoute(() => import("@/features/announcements"), (m) => m.AnnouncementDetailPage) },
    ],
  },

  // Tenant-scope — FP2b Anketler (Surveys). The admin surface carries three
  // panels (surveys list + launch + results · templates + create · pulse cycles
  // + create/pause/resume), gated on canManageSurveys (perm_ee_read-equivalent)
  // for the read surface and canAdminSurveys (perm_ee_admin) for the
  // create/launch affordances. The server enforces both regardless — a leaked
  // POST 403s; the rail hides /surveys without surveys.read. The employee twin
  // is /me/surveys (EmployeeSurveysIndex, its own endpoint — ADR-014 §2).
  {
    path: "/surveys",
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      // SEM-7199: yolsuz bir düzen katmanı — sekme çubuğunu (SurveysShellContent)
      // üç çocuğa da çizer ve adresi değiştirmez, böylece /surveys index olarak
      // kalır. Paneller `?tab=` yerine kendi adreslerini taşır; üst şerit onları
      // TEK sekmede tutar (`components/shell-tabs.ts`).
      {
        ...lazyRoute(() => import("@/features/surveys"), (m) => m.SurveysShellContent),
        children: [
          {
            index: true,
            ...lazyRoute(() => import("@/features/surveys"), (m) => m.SurveysListRoute),
          },
          {
            path: "templates",
            ...lazyRoute(() => import("@/features/surveys"), (m) => m.SurveysTemplatesRoute),
          },
          {
            path: "pulse",
            ...lazyRoute(() => import("@/features/surveys"), (m) => m.SurveysPulseRoute),
          },
        ],
      },
      // SEM-7244: sonuç ekranı kabuğun KARDEŞİDİR, çocuğu değil — sekme
      // şeridini taşımaz ve `SURVEYS_SHELL_TABS` içinde satırı yoktur (şerit
      // yalnız alt görünümleri sayar).
      {
        path: ":surveyId/results",
        ...lazyRoute(() => import("@/features/surveys"), (m) => m.SurveysResultsRoute),
      },
    ],
  },

  // Tenant-scope — SEM-6331 Raporlar & Analitik. The client surface for the
  // spec-09 report engine: dataset picker + query builder, interactive run,
  // background export, run ledger. Every authorization decision (column ACL at
  // plan stage, row/company narrow, definition visibility) belongs to the
  // server; the rail hides /reports without reports.read.
  {
    path: "/reports",
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      {
        index: true,
        ...lazyRoute(() => import("@/features/reports"), (m) => m.ReportsPage),
      },
    ],
  },

  { path: "*", element: <Navigate to="/" replace /> },
]);
